import { useAuth } from "@/context/AuthContext";
import { SaveDialogContent, SaveButton, SaveForm } from "@/components/SaveForm";
import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const inp = "bg-slate-950 border-slate-700 text-slate-100 mt-1";
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const monthNow = () => new Date().toISOString().slice(0, 7);
const todayLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function PaymentFields({ state, setter, tenants = [], includeTenant = false }) {
  const setField = (field, value) => setter((current) => ({ ...current, [field]: value }));
  const setStatus = (value) => setter((current) => ({
    ...current,
    status: value,
    pago_em: value === "PAGO" && !current.pago_em ? todayLocal() : current.pago_em,
  }));

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {includeTenant && (
        <div className="sm:col-span-2">
          <Label>Empresa *</Label>
          <Select value={state.tenant_id || ""} onValueChange={(v) => setField("tenant_id", v)}>
            <SelectTrigger className={inp}><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome_fantasia}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>Competência</Label>
        <Input
          type="month"
          className={inp}
          value={state.competencia || ""}
          onChange={(e) => setField("competencia", e.target.value)}
        />
      </div>

      <div>
        <Label>Vencimento</Label>
        <Input
          type="date"
          className={inp}
          value={state.vencimento || ""}
          onChange={(e) => setField("vencimento", e.target.value)}
        />
      </div>

      <div>
        <Label>Valor</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          className={inp}
          value={state.valor ?? ""}
          onChange={(e) => setField("valor", e.target.value)}
        />
      </div>

      <div>
        <Label>Status</Label>
        <div className="text-[11px] text-slate-500 mt-0.5">Atrasado é definido automaticamente pelo vencimento.</div>
        <Select value={state.status || "PENDENTE"} onValueChange={setStatus}>
          <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            {["PENDENTE","PAGO","CANCELADO"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Pago em {state.status === "PAGO" ? "*" : ""}</Label>
        <Input
          type="date"
          className={inp}
          value={state.pago_em || ""}
          onChange={(e) => setField("pago_em", e.target.value)}
        />
      </div>

      <div>
        <Label>Forma de pagamento</Label>
        <Input
          className={inp}
          value={state.forma_pagamento || ""}
          placeholder="PIX, boleto, cartão..."
          onChange={(e) => setField("forma_pagamento", e.target.value)}
        />
      </div>

      <div className="sm:col-span-2">
        <Label>Observações</Label>
        <Input
          className={inp}
          value={state.observacoes || ""}
          onChange={(e) => setField("observacoes", e.target.value)}
        />
      </div>
    </div>
  );
}

export default function PlatformPayments() {
  const { user } = useAuth();
  const owner = user?.is_platform_owner === true;
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    tenant_id: "", competencia: monthNow(), vencimento: "", valor: 0,
    status: "PENDENTE", pago_em: "", forma_pagamento: "", observacoes: "",
  });

  const load = async () => {
    try {
      const [p, t] = await Promise.all([api.get("/platform/payments"), api.get("/platform/tenants")]);
      setPayments(p.data); setTenants(t.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.tenant_id || !form.competencia || !form.vencimento) return toast.error("Informe empresa, competência e vencimento.");
    setBusy(true);
    try {
      await api.post("/platform/payments", form);
      toast.success("Pagamento lançado.");
      setOpen(false);
      setForm({ tenant_id: "", competencia: monthNow(), vencimento: "", valor: 0, status: "PENDENTE", pago_em: "", forma_pagamento: "", observacoes: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/platform/payments/${edit.id}`, edit);
      toast.success("Pagamento atualizado.");
      setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const removePayment = async (payment) => {
    const ok = window.confirm(
      `Remover a baixa deste pagamento para teste?\n\n${payment.empresa} · ${payment.competencia || "sem competência"} · ${brl(payment.valor)}\n\nA cobrança será mantida e voltará para PENDENTE ou ATRASADO conforme o vencimento.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/platform/payments/${payment.id}`);
      toast.success("Baixa removida. A cobrança foi recalculada pelo vencimento.");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Pagamentos</h1>
          <p className="text-sm text-slate-400">{owner ? "Controle manual de cobranças e recebimentos nesta primeira fase." : "Consulta de valores e pagamentos. Lançamentos, alterações e estornos são exclusivos do proprietário."}</p>
        </div>
        {owner && <Button onClick={() => setOpen(true)} className="bg-violet-700 hover:bg-violet-600"><Plus className="h-4 w-4 mr-1" /> Novo pagamento</Button>}
      </div>

      <Card className="card-metal p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
              <th className="text-left px-4 py-3">Empresa</th><th className="text-left px-4 py-3">Competência</th>
              <th className="text-left px-4 py-3">Vencimento</th><th className="text-right px-4 py-3">Valor</th>
              <th className="text-center px-4 py-3">Status</th><th className="text-left px-4 py-3">Pagamento</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-800">
                  <td className="px-4 py-3 font-medium text-slate-100">{p.empresa}</td>
                  <td className="px-4 py-3 text-slate-300">{p.competencia || "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{p.vencimento || "—"}</td>
                  <td className="px-4 py-3 text-right text-slate-100">{brl(p.valor)}</td>
                  <td className="px-4 py-3 text-center"><span className={p.status === "PAGO" ? "text-emerald-400" : p.status === "CANCELADO" ? "text-slate-500" : p.status === "ATRASADO" ? "text-rose-400" : "text-amber-400"}>{p.status}</span></td>
                  <td className="px-4 py-3 text-slate-400">{p.pago_em ? `${p.pago_em} · ${p.forma_pagamento || "—"}` : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {owner ? <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEdit({ ...p })}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => removePayment(p)}
                        className="text-rose-400 hover:text-rose-300 hover:bg-rose-950/40"
                        title="Remover baixa de pagamento para teste"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Remover baixa (teste)
                      </Button>
                    </div> : <span className="text-slate-400">Somente leitura</span>}
                  </td>
                </tr>
              ))}
              {!payments.length && <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">Nenhum pagamento lançado.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <SaveForm disabled={busy} onSave={create} className="space-y-4">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-violet-300" /> Novo pagamento</DialogTitle></DialogHeader>
            <PaymentFields state={form} setter={setForm} tenants={tenants} includeTenant />
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><SaveButton type="submit" disabled={busy} className="bg-violet-700 hover:bg-violet-600">Salvar</SaveButton></DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <SaveForm disabled={busy} onSave={saveEdit}
            className="space-y-4"
          >
            <DialogHeader><DialogTitle>Editar pagamento · {edit?.empresa}</DialogTitle></DialogHeader>
            {edit && <PaymentFields state={edit} setter={setEdit} />}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button>
              <SaveButton type="submit" disabled={busy} className="bg-violet-700 hover:bg-violet-600">Salvar alterações</SaveButton>
            </DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>
    </div>
  );
}
